import { createContext, useContext, useEffect, useState } from "react";
import { safeStorage } from "src/util/safe_storage";

type PrivacyModeContextType = {
  privacyMode: boolean;
  setPrivacyMode: (enabled: boolean) => void;
};

const initialState: PrivacyModeContextType = {
  privacyMode: false,
  setPrivacyMode: () => null,
};

const PrivacyModeContext = createContext<PrivacyModeContextType>(initialState);

export function PrivacyModeProvider({
  children,
  storageKey = "vite-ui-privacy-mode",
}: {
  children: React.ReactNode;
  storageKey?: string;
}) {
  const [privacyMode, setPrivacyModeState] = useState<boolean>(() => {
    return safeStorage.getBoolean(storageKey, false);
  });

  useEffect(() => {
    const root = window.document.documentElement;

    if (privacyMode) {
      root.classList.add("privacy-mode");
    } else {
      root.classList.remove("privacy-mode");
    }
  }, [privacyMode]);

  const value = {
    privacyMode,
    setPrivacyMode: (enabled: boolean) => {
      const result = safeStorage.setBoolean(storageKey, enabled);
      if (!result.success) {
        console.warn("Failed to save privacy mode preference:", result.error);
      }
      setPrivacyModeState(enabled);
    },
  };

  return (
    <PrivacyModeContext.Provider value={value}>
      {children}
    </PrivacyModeContext.Provider>
  );
}

export const usePrivacyMode = () => {
  const context = useContext(PrivacyModeContext);

  if (context === undefined)
    throw new Error("usePrivacyMode must be used within a PrivacyModeProvider");

  return context;
};
